/**
 * useImportsList — TanStack Query hook for the firm-wide imports list (R-F13).
 *
 * Keyed on `{owner, tag, q, search_id_thrive, exclude_status, exclude_tag,
 * page, page_size}` so any filter / scope / search / exclusion / page change
 * refetches. Runs through the ONE shared typed
 * `ImportsClient` (subtask 01) — today against MSW, repointed at the live API in
 * subtask 08 with no call-site change.
 */

import type {
  ImportListResponse,
  ImportStatus,
  ImportSummary,
  ImportTag,
} from "@nu-assist/contracts";
import { useQuery } from "@tanstack/react-query";

import type { ImportsClient, ListImportsParams } from "../../../clients/imports.client";

export interface UseImportsListArgs {
  client: ImportsClient;
  /** `you` | `all` | `<email>` — the canonical wire owner form. */
  owner: string;
  /**
   * Canonical tag wire value(s), or `null` to omit.
   *
   * A LIST means OR — the imports page's multi-select chips, which the server
   * applies as an array overlap. An empty list is the same as `null`: no chip
   * selected is no filter, not a filter that matches nothing. The BCT / AI
   * Review pickers still pass a single value; both shapes are the same request
   * as far as this hook is concerned, and `toListQueryKey` puts `"bct"` and
   * `["bct"]` on one cache entry so they cannot double-fetch.
   */
  tag: ImportTag | readonly ImportTag[] | null;
  /** Free-text search; trimmed/empty is omitted. */
  q: string;
  /**
   * Thrive search id from `?search_id_thrive=` (the My Searches row CTA), or
   * `null` to omit. Trimmed/empty behaves as `null` — a blank query-string
   * value is "no filter", never "the search whose id is the empty string".
   */
  searchIdThrive?: string | null;
  /**
   * One parse status to drop server-side. The pickers pass `"failed"`.
   * Subtractive rather than a whitelist: `parsing` / `uploaded` rows must stay
   * visible (usable shortly; hiding them reads as a lost file), only a terminal
   * failure is dropped. Server-side so `total` and the page agree — the whole
   * reason this list stopped filtering in the client.
   */
  excludeStatus?: ImportStatus | null;
  /**
   * Lifecycle tag(s) to drop server-side; a row carrying ANY of them is
   * dropped. The BCT picker passes `"bct"`: a BCT-derived import cannot be
   * used as the source of another tree (product rule, 2026-08-12), while AI
   * Review — same shared picker — omits it and keeps seeing those rows.
   * `"combined"` joins under a Raw chip (a combined list carries `raw` too).
   * Same subtractive, server-side shape as `excludeStatus`, for the same
   * page/total-agreement reason. An empty list is the same as `null`.
   */
  excludeTag?: ImportTag | readonly ImportTag[] | null;
  page: number;
  pageSize: number;
  sortKey?: string | null;
  sortDir?: "asc" | "desc" | null;
  /** Defaults to `true`. `false` keeps the query idle (no request, `data`
   *  undefined) — for callers whose params are only meaningful once something
   *  is selected, e.g. `useOutputFilenameSuggestion`'s version probe. */
  enabled?: boolean;
}

/** Build the wire params, omitting empties so the query key stays stable. */
export function toListParams(args: UseImportsListArgs): ListImportsParams {
  const params: ListImportsParams = {
    owner: args.owner,
    page: args.page,
    page_size: args.pageSize,
  };
  // An empty array is falsy-adjacent but NOT falsy, so the length check has to
  // be explicit: `params.tag = []` would serialize to nothing today and to
  // `?tag=` the moment `buildQuery` changed, and the "All" chip's state must
  // not depend on that.
  if (Array.isArray(args.tag)) {
    if (args.tag.length > 0) params.tag = args.tag;
  } else if (args.tag) {
    params.tag = args.tag;
  }
  const q = args.q.trim();
  if (q) params.q = q;
  const searchId = args.searchIdThrive?.trim();
  if (searchId) params.search_id_thrive = searchId;
  // No `.trim()`, unlike `q` and `searchIdThrive`: those are free text a user
  // typed, this is an `ImportStatus` union member. The trim was here while the
  // type was `string` — the narrowing is what makes it dead, and `tsc` said so.
  if (args.excludeStatus) params.exclude_status = args.excludeStatus;
  // Same empty-array discipline as `tag`: `[]` is "no exclusion", never sent.
  if (Array.isArray(args.excludeTag)) {
    if (args.excludeTag.length > 0) params.exclude_tag = args.excludeTag;
  } else if (args.excludeTag) {
    params.exclude_tag = args.excludeTag;
  }
  if (args.sortKey) params.sort_key = args.sortKey;
  if (args.sortDir) params.sort_dir = args.sortDir;
  return params;
}

/** A scalar tag or a list of them as one order-free key component. */
function tagSetKey(tag: string | readonly string[] | undefined): string | null {
  if (tag === undefined) return null;
  return [...(Array.isArray(tag) ? tag : [tag])].sort().join(",");
}

/**
 * The react-query cache key for one set of wire params.
 *
 * Exported so the tag normalization below is testable on its own. Every filter
 * that changes what the server returns belongs here, or two callers differing
 * only in that filter would share an entry and render each other's page.
 *
 * The tag component is SORTED AND JOINED, never the array itself. Two arrays
 * with the same members are never `===`, and react-query compares keys
 * structurally, so `[bct, ai]` and `[ai, bct]` would be two entries for one
 * result set — the reader toggling a chip off and back on would refetch over
 * data already in the cache, and the two entries would age independently. The
 * sort also collapses the picker's scalar `"bct"` onto the list's `["bct"]`,
 * which genuinely are the same request.
 */
export function toListQueryKey(params: ListImportsParams): readonly unknown[] {
  return [
    "imports",
    "list",
    params.owner,
    tagSetKey(params.tag),
    params.q ?? "",
    params.search_id_thrive ?? null,
    params.exclude_status ?? null,
    tagSetKey(params.exclude_tag),
    params.page,
    params.page_size,
    params.sort_key ?? null,
    params.sort_dir ?? null,
  ];
}

/**
 * How long after its enqueue a `parsing` upload keeps the list polling.
 *
 * Derived, not guessed. `import_sweeper_worker` fails a `parsing` upload whose
 * `updated_at` (stamped when `/complete` enqueued the parse, and again when the
 * worker's run starts) is more than 20 minutes old
 * (`STALE_PARSE_AFTER_SECONDS`), and runs every 5 minutes
 * (`ImportSweeperCron`). The list measures from the same `updated_at`, and the
 * worker's 15-minute Lambda timeout sits INSIDE the 20 (a run cannot outlive
 * the threshold, it is fenced if it tries), so it adds nothing: the latest the
 * sweep settles a row is 20 + 5 = 25 minutes after `updated_at`; 5 more is
 * margin for a slow tick. Past that the row is already swept, and the next
 * page load shows it; polling every 3 s for it helps nobody.
 *
 * The age is the BROWSER's `Date.now()` minus the server's `updated_at`, so
 * the 5-minute margin also absorbs clock skew between the two. A browser more
 * than ~5 minutes fast stops polling an upload before the sweep could have
 * settled it, and past 30 minutes fast it stops polling a brand-new one; the
 * row then updates on the next refetch (focus, reload). Accepted: a clock that
 * far off breaks more than this list.
 * `import_sweeper_worker/tests/test_calibration.py` fails if this drops below
 * the arithmetic.
 */
export const STALE_PARSE_POLL_CUTOFF_MS = 30 * 60 * 1000;

/**
 * The formats whose `parsing` belongs to `import_worker` and its sweeper
 * (`SWEPT_IMPORT_FORMATS`). `ai_review` / `combined` rows parse for hours under
 * their own sweepers, and the cutoff above must not apply to them.
 */
const SWEPT_FORMATS: ReadonlySet<string> = new Set(["baby_chops_v1", "bct"]);

/** Whether a row still has async work landing on it, so the list should poll. */
export function isRowUnsettled(row: ImportSummary, now: number): boolean {
  if (row.export_in_progress || row.status === "uploaded") return true;
  if (row.status !== "parsing") return false;
  if (!SWEPT_FORMATS.has(row.import_format)) return true;
  const since = Date.parse(row.updated_at);
  return Number.isNaN(since) || now - since <= STALE_PARSE_POLL_CUTOFF_MS;
}

export function useImportsList(args: UseImportsListArgs) {
  const params = toListParams(args);
  return useQuery<ImportListResponse>({
    // Filter changes (owner/tag/q/exclusions/page/page_size) drive the key →
    // refetch. BOTH `exclude_*` params belong here: they change what the server
    // returns, so two callers differing only in an exclusion must not share a
    // cache entry. Before they were added, the ImportsPage (no exclusions,
    // pageSize 5) and the pickers (`exclude_status=failed`, pageSize 8) only
    // avoided colliding because their page sizes differ — with the BCT picker
    // (`exclude_tag=bct`) and the AI Review picker both at pageSize 8, the
    // collision would be real: one would render the other's cached page.
    enabled: args.enabled ?? true,
    queryKey: toListQueryKey(params),
    queryFn: () => args.client.listImports(params),
    // Keep the previous page visible while the next page loads (no flicker).
    placeholderData: (prev) => prev,
    // Async work lands on rows after the fact: parse (import_worker) flips
    // `status` uploaded→parsing→ready, and export (export_worker) flips
    // per-contact state (`export_in_progress`). Poll while ANY visible row is
    // still parsing (either pre-parse state — a freshly-created row can
    // legitimately be caught at `uploaded` on a poll tick, not just
    // `parsing`) OR exporting, so the "Importing…" state (`ContactsStateBar`)
    // and the "Exporting…" pill refresh on their own; stop once every row is
    // settled.
    //
    // Except an upload stuck `parsing` past `STALE_PARSE_POLL_CUTOFF_MS`: the
    // stale-import sweep has settled it by then, and it no longer keeps the
    // list polling on its own.
    refetchInterval: (query) =>
      (query.state.data?.items ?? []).some((it) => isRowUnsettled(it, Date.now())) ? 3000 : false,
    // Keep polling while the tab is in the BACKGROUND, and this is the one
    // place in the app where that is not optional.
    //
    // TanStack pauses `refetchInterval` on a hidden document, and this app sets
    // `refetchOnWindowFocus: false` globally (main.tsx) — so a paused poll has
    // nothing to restart it. That was survivable while `parsing` meant a CSV
    // parse measured in seconds. It is not survivable now: AI Review sends the
    // recruiter to THIS list to wait out a scoring run measured in minutes, and
    // switching tabs while it runs is the obvious thing to do. Without this
    // they come back to a row still saying "Importing…" that will never move on
    // its own, having been told the file was on its way.
    //
    // The cost is bounded, but LESS than an earlier version of this comment
    // claimed, and the gap is worth stating rather than discovering:
    //
    //   * the ai-review and combine sweepers settle their own formats; a plain
    //     upload stranded in `parsing` by a dead `import_worker` is settled by
    //     `import_sweeper_worker` (epic 2026-09-30-import-worker-perf) and
    //     stops counting after `STALE_PARSE_POLL_CUTOFF_MS`. A row stranded in
    //     `uploaded` (the browser never called `/complete`) still has no
    //     sweeper, and the predicate above still polls on it;
    //   * the interval has no ceiling and no tick cap, so the worst case is
    //     one request every 3 s for ~3.5 h per open tab that can see an
    //     unsettled row — and `owner=all` makes that anyone's stuck import.
    //
    // A step-down (3 s for the first minutes, then 15-30 s) would cap all of
    // it at once and cost nothing in the real case; it is tracked as F3 in the
    // epic's follow-ups rather than done here, because it is wider than this
    // feature.
    //
    // Also note Chrome throttles hidden tabs hard (~1/min after 5 minutes, and
    // freezes them outright eventually), which jsdom cannot model — so this
    // option makes the backgrounded case WORK BETTER, not perfectly.
    refetchIntervalInBackground: true,
  });
}
